import React, { useMemo } from 'react';
import { RichTextBlock } from 'prismic-reactjs';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import Text from '@components/text';
import getLinkConfig from '@helpers/get-link-config';
import Wrapper from '@components/wrapper.styled';
import {
  Section,
  CardClip,
  Card,
  ContentSide,
  TitleWrapper,
  Actions,
  PrimaryButton,
  SecondaryButton,
  ImageSpacer,
  DesktopImage,
  MobileImage,
} from './text-image-cta.styled';

export interface LinkProps {
  url?: string;
  uid?: string;
  target?: string;
}

export interface ImageData {
  url?: string;
  alt?: string;
  gatsbyImageData?: IGatsbyImageData;
}

export interface TextImageCtaProps {
  title?: string;
  description?: RichTextBlock[];
  link?: LinkProps;
  linkLabel?: string;
  secondaryLink?: LinkProps;
  secondaryLinkLabel?: string;
  image?: ImageData;
  backgroundImage?: ImageData;
}

const TextImageCta: React.FC<TextImageCtaProps> = ({
  title,
  description = [],
  link,
  linkLabel,
  secondaryLink,
  secondaryLinkLabel,
  image,
  backgroundImage,
}) => {
  if (!title && !description?.length && !image?.url) {
    return null;
  }

  const primaryLinkConfig = useMemo(() => getLinkConfig(link), [link]);
  const secondaryLinkConfig = useMemo(() => getLinkConfig(secondaryLink), [secondaryLink]);

  const hasDescription = useMemo(
    () => (description?.length ?? 0) > 0 && description[0]?.text !== '',
    [description]
  );

  const hasPrimaryLink = useMemo(() => !!linkLabel, [linkLabel]);
  const hasSecondaryLink = useMemo(() => !!secondaryLinkLabel, [secondaryLinkLabel]);
  const imageUrl = image?.url;
  const bgImageUrl = backgroundImage?.url;

  return (
    <Section>
      <Wrapper>
        {/*
          CardClip:
          - position:relative + overflow:visible → DesktopImage (position:absolute)
            bleeds above the card top edge on desktop.
        */}
        <CardClip>
          <Card backgroundImage={bgImageUrl}>
            {/* Left column — text content + CTA buttons */}
            <ContentSide>
              <TitleWrapper>
                {title && (
                  <Text
                    tag="h2"
                    fontSize="heading5"
                    type="title"
                    stringText={title}
                    fontColor="white"
                    margin="none"
                  />
                )}

                {hasDescription && (
                  <Text type="description" richText={description} fontColor="white" margin="none" />
                )}
              </TitleWrapper>

              {(hasPrimaryLink || hasSecondaryLink) && (
                <Actions>
                  {hasPrimaryLink && (
                    <PrimaryButton
                      href={primaryLinkConfig.url || '#'}
                      target={link?.target || '_self'}
                      aria-label={linkLabel}
                    >
                      {linkLabel}
                    </PrimaryButton>
                  )}
                  {hasSecondaryLink && (
                    <SecondaryButton
                      href={secondaryLinkConfig.url || '#'}
                      target={secondaryLink?.target || '_self'}
                      aria-label={secondaryLinkLabel}
                    >
                      {secondaryLinkLabel}
                    </SecondaryButton>
                  )}
                </Actions>
              )}
            </ContentSide>

            {/* Spacer reserves the right column on desktop; hidden on mobile */}
            {imageUrl && <ImageSpacer />}

            {/*
              MobileImage — in-flow inside Card's flex-column.
              Rendered in DOM but display:none on desktop (CSS-only toggle).
              Shows below ContentSide on mobile (≤ 768px).
            */}
            {imageUrl && (
              <MobileImage src={imageUrl} alt={image?.alt || title || 'CTA image'} loading="lazy" />
            )}
          </Card>

          {/*
            DesktopImage — position:absolute anchored to CardClip (position:relative).
            Sits outside Card so Card's overflow:hidden does NOT clip it.
            height:115% bleeds above card top edge.
            display:none on mobile (CSS-only toggle).
          */}
          {imageUrl && (
            <DesktopImage
              src={imageUrl}
              alt={image?.alt || title || 'CTA image'}
              loading="lazy"
              aria-hidden="true"
            />
          )}
        </CardClip>
      </Wrapper>
    </Section>
  );
};

TextImageCta.defaultProps = {
  description: [],
};

export default React.memo(TextImageCta);
